import { combineReducers } from 'redux';

import storage from 'redux-persist/lib/storage';
import { persistReducer } from 'redux-persist';
import { withReduxStateSync } from 'redux-state-sync';

import { AuthActionTypes } from '@/store/types/auth.types';

import { authReducer } from '@/store/reducers/auth.reducer';
import { bulkCardCreationReducer } from '@/store/reducers/bulkCardCreation.reducer';
import { bulkCardCreationReportReducer } from '@/store/reducers/bulkCardCreationReport.reducer';
import { cardTransactionsReducer } from '@/store/reducers/cardTransactions.reducer';
import { dashboardConfigReducer } from '@/store/reducers/dashboard-config.reducer';
import { encryptionReducer } from '@/store/reducers/encryption.reducer';
import { integrationConfigReducer } from '@/store/reducers/integration-config.reducer';
import { modalReducer } from '@/store/reducers/modal.reducer';
import { navigationReducer } from '@/store/reducers/navigation.reducer';
import { onboardingUserReducer } from '@/store/reducers/onboarding-user.reducer';
import { p2pReducer } from '@/store/reducers/p2p.reducer';
import { passwordStrategyReducer } from '@/store/reducers/password-config.reducer';
import { payoutReducer } from '@/store/reducers/payout.reducer';
import { predefRestrictionsGroupReducer } from '@/store/reducers/predef-restrictions-group.reducer';
import { profileReducer } from '@/store/reducers/profile.reducer';
import { routeReducer } from '@/store/reducers/route.reducer';
import { searchHistoryReducer } from '@/store/reducers/search-history.reducer';
import { searchReducer } from '@/store/reducers/search.reducer';
import { supportUserReducer } from '@/store/reducers/support-user.reducer';
import { templateReducer } from '@/store/reducers/template.reducer';
import { themeReducer } from '@/store/reducers/theme.reducer';
import { usersReducer } from '@/store/reducers/users.reducer';
import { webhookReducer } from '@/features/webhooks/store/webhook.reducer';
import { alertReducer } from '@/store/reducers/alert.reducer';
import { AccessControlReducer } from '@/features/roles/store/reducers/accessControl.reducer';
import { sidebarReducer } from '@/router/store/sidebar.reducer';
import { kycNeedReviewReducer } from '@/features/kyc/store/reducers/kyc-need-review.reducer';
import { agentCardReducer } from '@/features/cards/store/reducers/agent-card.reducer';
import { agentWalletReducer } from '@/features/wallets/store/reducers/agent-wallet.reducer';
import { allBeneficiariesReducer } from '@/features/beneficiaries/store/reducers/all-beneficiaries.reducer';

const reducers = {
  auth: authReducer,
  modal: modalReducer,
  theme: themeReducer,
  agentCard: agentCardReducer,
  agentWallets: agentWalletReducer,
  encryption: encryptionReducer,
  supportUser: supportUserReducer,
  predefRestrictionsGroup: predefRestrictionsGroupReducer,
  webhook: webhookReducer,
  payout: payoutReducer,
  template: templateReducer,
  kycNeedReview: kycNeedReviewReducer,
  cardTransactions: cardTransactionsReducer,
  p2p: p2pReducer,
  bulkCardCreation: bulkCardCreationReducer,
  bulkCardCreationReport: bulkCardCreationReportReducer,
  onboardingUser: onboardingUserReducer,
  search: searchReducer,
  searchHistory: searchHistoryReducer,
  profile: profileReducer,
  passwordStrategy: passwordStrategyReducer,
  integrationConfig: integrationConfigReducer,
  users: usersReducer,
  dashboardConfig: dashboardConfigReducer,
  navigation: navigationReducer,
  route: routeReducer,
  alert: alertReducer,
  accessControl: AccessControlReducer,
  sidebar: sidebarReducer,
  allBeneficiaries: allBeneficiariesReducer,
};

export const appReducer = combineReducers(reducers);

const rootReducer = (state, action) => {
  // when a logout action is dispatched, the redux state will be reset.
  if (action.type === AuthActionTypes.AUTH_LOGOUT) {
    state = undefined;
  }

  return appReducer(state, action);
};

type TRootReducerType = typeof rootReducer;
export type TAppState = ReturnType<TRootReducerType>;

export const persistedReducer = persistReducer(
  {
    key: 'Dashboard-Connect',
    storage,
    blacklist: ['encryption', 'dashboardConfig', 'modal', 'alert'],
  },
  // Mandatory to match redux-state-sync and typesafe-actions types
  withReduxStateSync(rootReducer) as unknown as TRootReducerType,
);

export const getReducerHandlers = (reducerName: string): string[] =>
  reducers[reducerName]?.handlers && Object.keys(reducers[reducerName]?.handlers);
export const getReducersHandlers = (reducersName: string[]): string[] =>
  reducersName.reduce((acc, reducerName) => [...acc, ...(getReducerHandlers(reducerName) || [])], [] as string[]);
